import React, {useState, useEffect} from "react";
import {
    Box, Typography, Paper, CircularProgress, Chip, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, FormControl, InputLabel, Select, MenuItem, Button,
    Collapse, Pagination,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import {accountingApi} from "../../services/api/accounting";
import {getLandlordId} from "../../services/api/client";
import {propertiesApi} from "../../services/api/properties";

const REFERENCE_TYPES = [
    {value: "", label: "All Types"},
    {value: "RENT_PAYMENT", label: "Rent Payment"},
    {value: "EXPENSE", label: "Expense"},
    {value: "PENALTY", label: "Penalty"},
    {value: "ARREAR", label: "Arrear"},
    {value: "DEPOSIT", label: "Deposit"},
    {value: "DEPOSIT_REFUND", label: "Deposit Refund"},
    {value: "DEPOSIT_FORFEITED", label: "Deposit Forfeited"},
    {value: "DEPOSIT_INCOME", label: "Deposit Income"},
    {value: "LANDLORD_PAYOUT", label: "Landlord Payout"},
    {value: "MANUAL", label: "Manual"},
    {value: "MIGRATION", label: "Migration"},
];

const REF_TYPE_COLORS = {
    RENT_PAYMENT: "#2e7d32",
    EXPENSE: "#e65100",
    PENALTY: "#c62828",
    ARREAR: "#f57f17",
    DEPOSIT: "#1565c0",
    DEPOSIT_REFUND: "#00838f",
    DEPOSIT_FORFEITED: "#6a1b9a",
    DEPOSIT_INCOME: "#00695c",
    LANDLORD_PAYOUT: "#283593",
    MANUAL: "#616161",
    MIGRATION: "#795548",
};

const formatMoney = (amount) => {
    if (amount === 0 || amount === null || amount === undefined) return "-";
    return `KES ${Number(amount).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

function EntryRow({entry}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow sx={{cursor: "pointer", "&:hover": {bgcolor: "#f5f5f5"}}} onClick={() => setOpen(!open)}>
                <TableCell sx={{width: 40}}>
                    <IconButton size="small">{open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}</IconButton>
                </TableCell>
                <TableCell>{new Date(entry.entry_date).toLocaleDateString("en-KE", {year: "numeric", month: "short", day: "numeric"})}</TableCell>
                <TableCell sx={{maxWidth: 300}}>
                    <Typography variant="body2" noWrap>{entry.description}</Typography>
                </TableCell>
                <TableCell>
                    <Chip
                        label={entry.reference_type.replace(/_/g, " ")}
                        size="small"
                        sx={{
                            bgcolor: REF_TYPE_COLORS[entry.reference_type] || "#616161",
                            color: "#fff", fontWeight: 600, fontSize: "10px",
                        }}
                    />
                    {entry.is_reversed && <Chip label="REVERSED" size="small" color="error" variant="outlined" sx={{ml: 0.5, fontSize: "10px"}}/>}
                </TableCell>
                <TableCell align="right">
                    {formatMoney(entry.lines?.reduce((sum, l) => sum + (l.debit || 0), 0))}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={5} sx={{py: 0, borderBottom: open ? undefined : "none"}}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{py: 1.5, px: 2}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 600, color: "#666", fontSize: "12px"}}>Code</TableCell>
                                        <TableCell sx={{fontWeight: 600, color: "#666", fontSize: "12px"}}>Account</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 600, color: "#1565c0", fontSize: "12px"}}>Debit</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 600, color: "#2e7d32", fontSize: "12px"}}>Credit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entry.lines?.map((line) => (
                                        <TableRow key={line.id}>
                                            <TableCell sx={{fontFamily: "monospace", fontSize: "12px"}}>{line.code}</TableCell>
                                            <TableCell sx={{fontSize: "12px"}}>{line.name}</TableCell>
                                            <TableCell align="right" sx={{color: "#1565c0", fontSize: "12px"}}>{line.debit > 0 ? formatMoney(line.debit) : "-"}</TableCell>
                                            <TableCell align="right" sx={{color: "#2e7d32", fontSize: "12px"}}>{line.credit > 0 ? formatMoney(line.credit) : "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function JournalEntries() {
    const [loading, setLoading] = useState(true);
    const [entries, setEntries] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [referenceType, setReferenceType] = useState("");
    const [propertyId, setPropertyId] = useState("");
    const [properties, setProperties] = useState([]);

    useEffect(() => {
        fetchProperties();
    }, []);

    useEffect(() => {
        fetchEntries();
    }, [page]);

    const fetchProperties = async () => {
        try {
            const landlordId = getLandlordId();
            const response = await propertiesApi.getAll(landlordId);
            if (response.ok && Array.isArray(response.data)) {
                setProperties(response.data);
            }
        } catch (err) {
            console.error("Failed to fetch properties:", err);
        }
    };

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const params = {
                landlord_id: getLandlordId(),
                page,
                page_size: 25,
            };
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (referenceType) params.reference_type = referenceType;
            if (propertyId) params.property_id = parseInt(propertyId);

            const response = await accountingApi.getJournalEntries(params);
            if (response.ok && response.data) {
                setEntries(response.data.entries || []);
                setTotalPages(response.data.total_pages || 1);
            }
        } catch (err) {
            console.error("Failed to fetch journal entries:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        setPage(1);
        fetchEntries();
    };

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                Journal Entries
            </Typography>

            {/* Filters */}
            <Paper sx={{p: 2, mb: 3, borderRadius: 2}}>
                <Box sx={{display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center"}}>
                    <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{shrink: true}} size="small" sx={{width: 170}}/>
                    <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{shrink: true}} size="small" sx={{width: 170}}/>
                    <FormControl size="small" sx={{minWidth: 170}}>
                        <InputLabel>Type</InputLabel>
                        <Select value={referenceType} label="Type" onChange={(e) => setReferenceType(e.target.value)}>
                            {REFERENCE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{minWidth: 180}}>
                        <InputLabel>Property</InputLabel>
                        <Select value={propertyId} label="Property" onChange={(e) => setPropertyId(e.target.value)}>
                            <MenuItem value="">All Properties</MenuItem>
                            {properties.map(p => <MenuItem key={p.property_id} value={p.property_id}>{p.property_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button variant="contained" startIcon={<FilterListIcon/>} onClick={handleFilter} size="small"
                        sx={{background: "linear-gradient(45deg, #2C5283, #1E3A63)", textTransform: "none"}}>
                        Apply
                    </Button>
                </Box>
            </Paper>

            {/* Table */}
            <Paper sx={{borderRadius: 2, overflow: "hidden"}}>
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
                ) : entries.length === 0 ? (
                    <Box sx={{py: 6, textAlign: "center"}}><Typography color="textSecondary">No journal entries found for this period.</Typography></Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{bgcolor: "#f5f7fa"}}>
                                        <TableCell sx={{width: 40}}/>
                                        <TableCell sx={{fontWeight: 600, width: 130}}>Date</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Description</TableCell>
                                        <TableCell sx={{fontWeight: 600, width: 160}}>Type</TableCell>
                                        <TableCell sx={{fontWeight: 600, width: 150}} align="right">Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entries.map(entry => <EntryRow key={entry.id} entry={entry}/>)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalPages > 1 && (
                            <Box sx={{display: "flex", justifyContent: "center", py: 2}}>
                                <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary"/>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
}
